/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
    Card,
    CoinsIcon,
    DaButton,
    Image,
    InternalGrid,
    MarkerIcon,
    ThemeDefault,
    Message,
    InformationIcon,
    ModalControl,
    ModalProvider,
} from '@paygreen/paygreen-ui';
import { Link as RouterLink } from 'react-router-dom';
import { getNumberFromThemeValue } from '../../utils/tools';
import { useNodeDimensions } from '../../utils/hooks/useNodeDimensions';
import { Loader, CategoryTitle } from '../../components';
import forestProjectImage from '../../assets/images/forestProject.jpg';
import ConfirmDeleteModalProject from './ConfirmDeleteModalProject';
import ProjectName from './ProjectName';
import { useAPIError, useUser } from '../../context';
import { getStatByProject } from './service';
import OddModal from './OddModal';
import { CheckIconStyled, ImageStyled } from './Project.styled';

import dayjs from '../../utils/dayjsHelper';
import i18n from '../../internationalization/i18n';

import { frLangLocale } from '../../utils/constants';
import getMainSdg from '../../utils/getMainSdg';

const CompletedMode = styled.div`
    .gray-filter {
        filter: ${(props) =>
            props.isActive === '0' ? 'grayscale(1)' : 'grayscale(0)'};
    }

    img {
        filter: ${(props) =>
            props.isActive === '0' ? 'opacity(0.7)' : 'opacity(1)'};
    }
`;

const ProjectsCard = ({
    userProjects,
    t,
    isOpen,
    project,
    isLoading,
    renderCardConfig,
    renderCardContent,
    setIsLoading,
    projects,
    setProjects,
    fetchData,
}) => {
    const [userContribution, setUserContribution] = useState();
    const [clientContribution, setClientContribution] = useState();

    const { addError } = useAPIError();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const cardProps = renderCardConfig(project);

    const [errorMessage] = useState();

    const { user } = useUser();

    const renderErrorMessage = () => {
        if (errorMessage && errorMessage.detail) {
            return (
                <Message
                    message={errorMessage.detail}
                    type="error"
                    paddingLateral="lg"
                    alignText="center"
                />
            );
        }
        return null;
    };

    const getStatData = () => {
        const { carbonPrice, idProject } = project;

        if (!userContribution && !clientContribution && carbonPrice) {
            getStatByProject(idProject)
                .then((res) => {
                    const { data } = res;

                    if (carbonPrice) {
                        const consumerContribution = (
                            Number.parseFloat(
                                data[0]?.carbonOffsetFromConsumer * carbonPrice,
                            ) / 100 || 0
                        ).toFixed(2);

                        const sellerContribution = Number.parseFloat(
                            (data[0]?.carbonOffset * carbonPrice) / 100 || 0,
                        ).toFixed(2);

                        setUserContribution(consumerContribution);
                        setClientContribution(sellerContribution);
                    }
                })
                .catch((error) => {
                    addError(
                        error?.response?.data.detail,
                        error?.response?.data.status,
                    );
                });
        }
    };

    useEffect(() => {
        isMounted.current = true;

        getMainSdg(user?.idUser?.toString());

        getStatData();
    }, []);

    const renderCardHeader = () => {
        return (
            <Card {...cardProps}>
                <Image className="gray-filter" imageType="cover">
                    <img src={forestProjectImage} alt="project view" />
                </Image>
                <InternalGrid
                    displayType="flex"
                    style={{
                        flexDirection: 'column',
                        height: '100%',
                        placeItems: 'center',
                    }}
                    justifyContent="space-between"
                    alignItems="flex-end"
                >
                    <InternalGrid style={{ margin: '1rem 0 1rem 0' }}>
                        <ProjectName
                            project={project}
                            userProjects={userProjects}
                        />
                    </InternalGrid>

                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns={
                            width >
                            getNumberFromThemeValue(ThemeDefault.blockWidth.md)
                                ? 'repeat(3, fit-content(100%))'
                                : 'auto auto'
                        }
                        justifyItems="start"
                        alignItems="center"
                        gridGap={
                            width >
                            getNumberFromThemeValue(ThemeDefault.blockWidth.md)
                                ? 'lg'
                                : 'sm'
                        }
                        style={{ margin: '1rem 0 1rem 0' }}
                    >
                        <CategoryTitle
                            category={t(`countries:${project.country}`)}
                            icon={
                                <MarkerIcon
                                    iconSize="md"
                                    colorPallet="wab"
                                    colorWab="grey40"
                                />
                            }
                        />

                        <CategoryTitle
                            category={project.carbonPrice / 100}
                            icon={
                                <CoinsIcon
                                    iconSize="md"
                                    colorPallet="wab"
                                    colorWab="grey40"
                                />
                            }
                        >
                            {t(
                                'contributions.carbonProjects.projectCard.carbonPrice',
                            )}
                        </CategoryTitle>
                    </InternalGrid>

                    {userProjects && (
                        <InternalGrid
                            justifyContent="center"
                            childrenMarginTop="md"
                            style={{ margin: '1rem 0 1rem 0' }}
                        >
                            <RouterLink to={`/projects/${project.idProject}`}>
                                <DaButton
                                    buttonSize="md"
                                    buttonStyle="line"
                                    gradient="theme"
                                    colorTheme="secondary"
                                    icon={<InformationIcon />}
                                >
                                    {t(
                                        'contributions.globalView.globalCard.moreInfo',
                                    )}
                                </DaButton>
                            </RouterLink>
                        </InternalGrid>
                    )}

                    <InternalGrid
                        childrenMarginTop="md"
                        justifyContent="center"
                    >
                        {renderProjectGoal()}
                    </InternalGrid>

                    <InternalGrid>
                        <ConfirmDeleteModalProject
                            userProjects={userProjects}
                            isOpen={isOpen}
                            project={project}
                            projects={projects}
                            setProjects={setProjects}
                            fetchData={fetchData}
                            setIsLoading={setIsLoading}
                        />
                    </InternalGrid>

                    {renderErrorMessage()}
                </InternalGrid>
                <InternalGrid justifyContent="center" childrenMarginTop="lg">
                    {renderCardContent(
                        project,
                        userContribution,
                        clientContribution,
                    )}
                </InternalGrid>
            </Card>
        );
    };

    const renderCard = () => {
        return (
            <CompletedMode ref={ref} isActive={project.isActive}>
                {renderCardHeader()}
            </CompletedMode>
        );
    };

    // we create a ref using a special hook to get width from Card component
    const [ref, { width }] = useNodeDimensions();

    const renderToltipTitle = (goal) => {
        const userLanguage = dayjs.locale(frLangLocale.includes(i18n.language));

        if (userLanguage === 'fr') {
            return `${t('contributions.globalView.number')} - ${goal.id} ${
                goal.nameFrench
            }`;
        }
        return `${t('contributions.globalView.number')} - ${goal.id} ${
            goal.nameEnglish
        }`;
    };

    const renderProjectGoal = () => {
        if (project?.sustainableDevelopmentGoals) {
            return project?.sustainableDevelopmentGoals.map((goal) => {
                return (
                    <ModalProvider key={goal.id}>
                        <ModalControl hasOnClickControl={userProjects}>
                            <ImageStyled
                                blockHeight="tiny"
                                blockWidth="tiny"
                                userProjects={userProjects}
                                className="gray-filter"
                                imageType="cover"
                                marginLeft="sm"
                                marginRight="sm"
                                title={renderToltipTitle(goal)}
                            >
                                <img src={goal.iconUri} alt="project view" />

                                {project.selectedSDG === goal.id && (
                                    <CheckIconStyled
                                        isActive
                                        hasBackground={true}
                                        colorPallet="status"
                                        colorStatus="success"
                                        iconSize="xs"
                                    />
                                )}
                            </ImageStyled>
                        </ModalControl>

                        <ModalControl hasOnClickControl>
                            <OddModal
                                name={project.name}
                                goals={project.sustainableDevelopmentGoals}
                                fetchData={fetchData}
                                selectedSDG={project.selectedSDG}
                                idProject={project.idProject}
                            />
                        </ModalControl>
                    </ModalProvider>
                );
            });
        }
        return null;
    };

    return (
        <>
            {isLoading ? <Loader isLoading={isLoading} /> : <>{renderCard()}</>}
        </>
    );
};

ProjectsCard.propTypes = {
    blockWidth: PropTypes.string,
    carbonPrice: PropTypes.number,
    carbonOffsetValue: PropTypes.number,

    isCompleted: PropTypes.bool,
    userProjects: PropTypes.bool,
    isUserActiveProject: PropTypes.bool,
};

ProjectsCard.defaultProps = {
    carbonPrice: 0,
    carbonOffsetValue: 0,
    isCompleted: false,
    userProjects: false,
    isUserActiveProject: false,
};

export default withTranslation('common')(ProjectsCard);
